import { type DocumentMeta } from "@prismicio/editor-fields"
import { useRequest } from "@prismicio/editor-support/Suspense"
import {
  Button,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  ErrorBoundary,
  Text
} from "@prismicio/editor-ui"
import { Suspense, useState } from "react"

import { getBaseRepositoryUrl, getRepositoryName } from "../../app/routing"
import { getPreviewUrls, type PreviewUrl } from "../../app/services/core"
import { useOpenRedirectUrl } from "../../document/LeftSidePanel/PreviewMenu"
import { useHasSettingsPermission } from "../DocumentList.utils"

interface ReleasePreviewProps {
  releaseId: string | undefined
  size?: "small" | "large"
  releaseDocument?: DocumentMeta
  singlePreviewOption?: boolean
}

export function ReleasePreview(props: ReleasePreviewProps) {
  const {
    releaseId,
    size = "large",
    releaseDocument,
    singlePreviewOption
  } = props

  if (!releaseDocument)
    return <ReleasePreviewDropdownMenuFallback size={size} />

  return (
    <ErrorBoundary
      renderError={() => <ReleasePreviewDropdownMenuFallback size={size} />}
    >
      <Suspense fallback={<ReleasePreviewDropdownMenuFallback size={size} />}>
        <ReleasePreviewMenu
          releaseId={releaseId}
          size={size}
          releaseDocument={releaseDocument}
          singlePreviewOption={singlePreviewOption}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

interface ReleasePreviewMenuProps extends ReleasePreviewProps {
  releaseDocument: DocumentMeta
}

function ReleasePreviewMenu(props: ReleasePreviewMenuProps) {
  const {
    releaseId,
    size,
    releaseDocument,
    singlePreviewOption = false
  } = props

  const [showConfigurationDialog, setShowConfigurationDialog] = useState(false)
  const { results: previewOptions } = useRequest(getPreviewUrls, [])
  const { isRedirectUrlLoading, openRedirectUrl } = useOpenRedirectUrl()
  const roleCanAccessSettings = useHasSettingsPermission()

  function openPreview(previewOption: PreviewUrl) {
    const releaseDocumentVersion = releaseDocument.versions[0]

    if (!releaseDocumentVersion) return

    /**
     * To open the redirect url in a preview page we take the first document and version in the release
     * and open the redirect url for that document and version
     */
    openRedirectUrl({
      previewOption,
      documentMeta: releaseDocument,
      documentVersion: releaseDocumentVersion
    })
  }

  function onClickSinglePreviewButton() {
    const previewOption = previewOptions[0]
    if (previewOption) {
      openPreview(previewOption)
      return
    }

    // No preview option: show configuration modal
    setShowConfigurationDialog(true)
  }

  const hasPreviewOptions = previewOptions.length > 0

  return (
    <>
      {singlePreviewOption ? (
        <Button
          size={size}
          color="grey"
          startIcon="playCircle"
          loading={isRedirectUrlLoading}
          onClick={onClickSinglePreviewButton}
        >
          Preview
        </Button>
      ) : (
        <ReleasePreviewDropdownMenu
          releaseId={releaseId}
          size={size}
          loading={isRedirectUrlLoading}
          previewOptions={previewOptions}
          hasPreviewOptions={hasPreviewOptions}
          openPreview={openPreview}
          openPreviewConfigurationDialog={() =>
            setShowConfigurationDialog(true)
          }
        />
      )}

      <NoPreviewConfiguredDialog
        open={showConfigurationDialog}
        onOpenChange={setShowConfigurationDialog}
        hasSettingsPermissions={roleCanAccessSettings}
      />
    </>
  )
}

interface ReleasePreviewDropdownMenuProps {
  releaseId: string | undefined
  size?: "small" | "large"
  loading: boolean
  hasPreviewOptions: boolean
  previewOptions: PreviewUrl[]
  openPreview: (previewOption: PreviewUrl) => void
  openPreviewConfigurationDialog: () => void
}

function ReleasePreviewDropdownMenu(props: ReleasePreviewDropdownMenuProps) {
  const {
    releaseId,
    size,
    loading,
    hasPreviewOptions,
    previewOptions,
    openPreview,
    openPreviewConfigurationDialog
  } = props
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button
          size={size}
          color="grey"
          startIcon="playCircle"
          endIcon="arrowDropDown"
          disabled={!releaseId}
          loading={loading}
          onClick={() => {
            if (!hasPreviewOptions) {
              openPreviewConfigurationDialog()
            }
          }}
        >
          Preview
        </Button>
      </DropdownMenuTrigger>

      {hasPreviewOptions && (
        <DropdownMenuContent align="start" maxHeight={256} minWidth={256}>
          <DropdownMenuLabel>
            <Text color="grey11">Preview</Text>
          </DropdownMenuLabel>

          {previewOptions.map((previewOption) => (
            <DropdownMenuItem
              key={previewOption.id}
              onSelect={() => openPreview(previewOption)}
            >
              <Text href={previewOption.url} color="grey12">
                {previewOption.label}
              </Text>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  )
}

interface ReleasePreviewDropdownMenuFallbackProps {
  size?: "small" | "large"
}

function ReleasePreviewDropdownMenuFallback(
  props: ReleasePreviewDropdownMenuFallbackProps
) {
  const { size = "large" } = props

  return (
    <Button
      size={size}
      color="grey"
      startIcon="playCircle"
      endIcon="arrowDropDown"
      disabled
    >
      Preview
    </Button>
  )
}

interface NoPreviewConfiguredDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  hasSettingsPermissions: boolean
}

function NoPreviewConfiguredDialog(props: NoPreviewConfiguredDialogProps) {
  const { open, onOpenChange, hasSettingsPermissions } = props

  const repositoryId = getRepositoryName()
  const baseRepoUrl = getBaseRepositoryUrl({ repositoryId })

  const previewSettingUrl = `${baseRepoUrl}/settings/previews/`

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="small">
      <DialogHeader title="Want to preview your changes?" />
      <DialogContent padding={16}>
        <Text>
          No previews configured. Please configure your{" "}
          <Text variant="bold">previews</Text> to enable this feature.
        </Text>
      </DialogContent>
      <DialogActions>
        <DialogCancelButton />
        <DialogActionButton
          tooltipLabel={
            !hasSettingsPermissions
              ? "You are missing rights to edit these settings."
              : undefined
          }
          onClick={() => (window.location.href = previewSettingUrl)}
          disabled={!hasSettingsPermissions}
        >
          Go to settings
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}
